import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import axios from 'axios';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: 'operator',
    department: '',
    email: '',
    full_name: ''
  });

  const roles = [
    { value: 'admin', label: 'Amministratore' },
    { value: 'operator', label: 'Operatore' },
    { value: 'viewer', label: 'Visualizzatore' }
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Errore nel caricamento utenti:', error);
      showSnackbar('Errore nel caricamento utenti', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username || '',
        password: '',
        confirmPassword: '',
        role: user.role || 'operator',
        department: user.department || '',
        email: user.email || '',
        full_name: user.full_name || ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        confirmPassword: '',
        role: 'operator',
        department: '',
        email: '',
        full_name: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      confirmPassword: '',
      role: 'operator',
      department: '',
      email: '',
      full_name: ''
    });
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      showSnackbar('Il nome utente è obbligatorio', 'error');
      return false;
    }

    if (!editingUser && !formData.password) {
      showSnackbar('La password è obbligatoria per nuovi utenti', 'error');
      return false;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      showSnackbar('Le password non corrispondono', 'error');
      return false;
    }

    if (formData.password && formData.password.length < 6) {
      showSnackbar('La password deve essere almeno di 6 caratteri', 'error');
      return false;
    }

    if (formData.email && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      showSnackbar('Formato email non valido', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const userData = {
        username: formData.username.trim(),
        role: formData.role,
        department: formData.department.trim() || null,
        email: formData.email.trim() || null,
        full_name: formData.full_name.trim() || null
      };

      if (formData.password) {
        userData.password = formData.password;
      }

      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, userData);
        showSnackbar('Utente aggiornato con successo', 'success');
      } else {
        await axios.post('/api/users', userData);
        showSnackbar('Utente creato con successo', 'success');
      }

      handleCloseDialog();
      loadUsers();
    } catch (error) {
      console.error('Errore nel salvataggio utente:', error);
      showSnackbar(
        error.response?.data?.message || 'Errore nel salvataggio utente', 
        'error'
      );
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo utente?')) {
      try {
        await axios.delete(`/api/users/${userId}`);
        showSnackbar('Utente eliminato con successo', 'success');
        loadUsers();
      } catch (error) {
        console.error('Errore nell\'eliminazione utente:', error);
        showSnackbar(
          error.response?.data?.message || 'Errore nell\'eliminazione utente', 
          'error'
        );
      }
    }
  };

  const getRoleChip = (role) => {
    const roleConfig = {
      'admin': { label: 'Amministratore', color: 'error', icon: <AdminIcon fontSize="small" /> },
      'operator': { label: 'Operatore', color: 'primary', icon: <PersonIcon fontSize="small" /> },
      'viewer': { label: 'Visualizzatore', color: 'default', icon: <PersonIcon fontSize="small" /> }
    };

    const config = roleConfig[role] || roleConfig['operator'];
    return <Chip label={config.label} color={config.color} size="small" icon={config.icon} />;
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'username', headerName: 'Username', width: 150 },
    { field: 'full_name', headerName: 'Nome Completo', width: 200 },
    { field: 'email', headerName: 'Email', width: 200 },
    {
      field: 'role',
      headerName: 'Ruolo',
      width: 150,
      renderCell: (params) => getRoleChip(params.value)
    },
    { field: 'department', headerName: 'Reparto', width: 130 },
    {
      field: 'created_at',
      headerName: 'Data Creazione',
      width: 150,
      valueFormatter: (params) => {
        return new Date(params.value).toLocaleDateString('it-IT');
      }
    },
    {
      field: 'last_login',
      headerName: 'Ultimo Accesso',
      width: 150,
      valueFormatter: (params) => {
        return params.value ? new Date(params.value).toLocaleDateString('it-IT') : 'Mai';
      }
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 150,
      sortable: false,
      renderCell: (params) => {
        // Non permettere di eliminare se stessi
        const canDelete = params.row.username !== 'administrator';
        
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Modifica">
              <IconButton
                size="small"
                color="info"
                onClick={() => handleOpenDialog(params.row)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            {canDelete && (
              <Tooltip title="Elimina">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(params.row.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AdminIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Gestione Utenti
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadUsers}
            >
              Aggiorna
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nuovo Utente
            </Button>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Solo gli amministratori possono gestire gli utenti del sistema.
        </Alert>

        <Box sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={users}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
            localeText={{
              noRowsLabel: 'Nessun utente trovato',
              footerRowSelected: (count) => `${count} riga${count !== 1 ? 'he' : ''} selezionata${count !== 1 ? 'e' : ''}`,
              footerTotalRows: 'Righe totali:',
              columnMenuSortAsc: 'Ordine crescente',
              columnMenuSortDesc: 'Ordine decrescente',
              columnMenuFilter: 'Filtro',
              columnMenuHideColumn: 'Nascondi colonna',
              columnMenuShowColumns: 'Mostra colonne'
            }}
          />
        </Box>

        {/* Dialog per aggiungere/modificare utente */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingUser ? 'Modifica Utente' : 'Nuovo Utente'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Username *"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                disabled={!!editingUser} // Non permettere di cambiare username in modifica
              />
              
              <TextField
                fullWidth
                label="Nome Completo"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={editingUser ? "Nuova Password (lascia vuoto per mantenere)" : "Password *"}
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                />
                <TextField
                  fullWidth
                  label="Conferma Password"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required={!!formData.password}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  select
                  label="Ruolo *"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  {roles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  label="Reparto"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </Box>

              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annulla</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingUser ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar per messaggi */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default UserManagement;